/**
 * Top bar: page title, theme switch, alerts bell, user menu.
 * Ported from the mockup's components/header.js.
 */
import { useEffect, useRef, useState }  from "react";
import { useNavigate }                  from "react-router-dom";
import styles                           from "./Header.module.scss";
import { useAuth }                      from "@features/auth/AuthContext";
import { useTheme, THEMES, type Theme } from "@shared/hooks/useTheme";
import { ConfirmDialog }                from "@shared/ui";

interface HeaderProps {
  title: string;
  /** Optional right-side slot for page-specific controls. */
  actions?: React.ReactNode;
  alertCount?: number;
  /** Hide the alerts bell — e.g. platform admins have no per-org alerts. */
  showBell?: boolean;
  /** Custom bell element (e.g. the resource-alerts dropdown). Overrides the
      static bell when provided; showBell:false still hides it entirely. */
  bell?: React.ReactNode;
}

const THEME_ICON: Record<Theme, string> = {
  light: "fa-sun",
  dark: "fa-moon",
  soft: "fa-cloud",
  contrast: "fa-circle-half-stroke",
};

export const Header: React.FC<HeaderProps> = ({ title, actions, alertCount = 0, showBell = true, bell }) => {
  const { user, signOut } = useAuth();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const [themeOpen, setThemeOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const [confirmLogout, setConfirmLogout] = useState(false);
  const rootRef = useRef<HTMLElement>(null);

  // One listener closes whichever menu is open.
  useEffect(() => {
    if (!themeOpen && !userOpen) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current?.contains(e.target as Node)) return;
      setThemeOpen(false);
      setUserOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setThemeOpen(false);
        setUserOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [themeOpen, userOpen]);

  return (
    <header className={styles.topbar} ref={rootRef}>
      <div className={styles.titleWrap}>
        {/* Small back button to the left of the title — history.back() via
            useNavigate(-1). Styled as a bordered icon tile so it reads as
            a chrome affordance rather than an inline link. */}
        <button 
          type="button"
          className={styles.backBtn}
          onClick={() => navigate(-1)}
          aria-label="Go back"
          title="Go back"
        >
          <i className="fas fa-chevron-left" />
        </button>
        <h1 className={styles.title}>{title}</h1>
      </div>

      <div className={styles.tools}>
        {actions}

        {/* theme */}
        <div className={styles.menuWrap}>
          <button
            className={styles.pill}
            onClick={() => { setThemeOpen((o) => !o); setUserOpen(false); }}
            aria-haspopup="menu"
            aria-expanded={themeOpen}
          >
            <i className={`fas ${THEME_ICON[theme]}`} />
            <span className={styles.pillLabel}>
              {theme[0]?.toUpperCase()}{theme.slice(1)}
            </span>
            <i className={`fas fa-chevron-down ${styles.caret}`} />
          </button>
          {themeOpen && (
            <div className={styles.drop} role="menu">
              <div className={styles.dropHead}>Theme</div>
              {THEMES.map((t) => (
                <button
                  key={t}
                  role="menuitem"
                  className={`${styles.dropRow} ${t === theme ? styles.dropRowActive : ""}`}
                  onClick={() => { setTheme(t); setThemeOpen(false); }}
                >
                  <i className={`fas ${THEME_ICON[t]}`} />
                  <span>{t[0]?.toUpperCase()}{t.slice(1)}</span>
                  {t === theme && <i className={`fas fa-check ${styles.check}`} />}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* alerts */}
        {showBell && (
          bell ?? (
            <button
              className={`${styles.bell} ${alertCount > 0 ? styles.bellActive : ""}`}
              title="Active alerts"
              aria-label={`${alertCount} active alerts`}
            >
              <i className="fas fa-bell" />
              {alertCount > 0 && (
                <span className={styles.bellCount}>{alertCount > 99 ? "99+" : alertCount}</span>
              )}
            </button>
          )
        )}

        {/* user */}
        <div className={styles.menuWrap}>
          <button
            className={styles.userBtn}
            onClick={() => { setUserOpen((o) => !o); setThemeOpen(false); }}
            aria-haspopup="menu"
            aria-expanded={userOpen}
          >
            <span className={styles.userName}>{user?.name}</span>
            <i className={`fas fa-chevron-down ${styles.caret}`} />
          </button>
          {userOpen && (
            <div className={`${styles.drop} ${styles.dropRight}`} role="menu">
              <div className={styles.dropHead}>Account</div>
              <div className={styles.userCard}>
                <div className={styles.userMeta}>
                  <span className={styles.userCardName}>{user?.name}</span>
                  <span className={styles.userCardRole}>{user?.role_label}</span>
                </div>
              </div>
              <button
                className={`${styles.dropRow} ${styles.danger}`}
                role="menuitem"
                onClick={() => { setUserOpen(false); setConfirmLogout(true); }}
              >
                <i className="fas fa-right-from-bracket" />
                <span>Sign out</span>
              </button>
            </div>
          )}
        </div>
      </div>
      {confirmLogout && (
        <ConfirmDialog
          title="Confirm Logout"
          text="Are you sure you want to logout? You will need to sign in again to access your account."
          tone="danger"
          icon="fa-right-from-bracket"
          confirmLabel="Logout"
          cancelLabel="Cancel"
          onConfirm={() => { setConfirmLogout(false); void signOut(); }}
          onCancel={() => setConfirmLogout(false)}
        />
      )}
    </header>
  );
};
